import { useState, useCallback, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Armchair, Plus, RefreshCw, Trash2, Edit, Loader2, Sparkles, Accessibility, Baby,
  Timer, Settings, CheckCircle2, AlertTriangle, Wrench,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ZONES = ["Main", "Window", "Family", "Private", "Outdoor", "Bar", "VIP"];
// Statuses a Center Manager may set. Occupied & Reserved are excluded on
// purpose — they are owned by Booking Intelligence's assign/seat/release
// lifecycle. Blocked (aka Maintenance / Out of Service) uses the
// normalised "out_of_service" key so the audit log stays consistent.
const CM_STATUSES = [
  { value: "available",       label: "Available" },
  { value: "cleaning",        label: "Cleaning" },
  { value: "out_of_service",  label: "Out of Service" },
];
const ADMIN_STATUSES = [
  { value: "available",       label: "Available" },
  { value: "reserved",        label: "Reserved" },
  { value: "occupied",        label: "Occupied" },
  { value: "cleaning",        label: "Cleaning" },
  { value: "out_of_service",  label: "Out of Service" },
];

const statusBadge = (s) => {
  const map = {
    available:      "bg-emerald-500/20 text-emerald-500 border-emerald-500/30",
    reserved:       "bg-amber-500/20 text-amber-500 border-amber-500/30",
    occupied:       "bg-red-500/20 text-red-500 border-red-500/30",
    cleaning:       "bg-blue-500/20 text-blue-500 border-blue-500/30",
    out_of_service: "bg-slate-500/20 text-slate-500 border-slate-500/30",
    blocked:        "bg-slate-500/20 text-slate-500 border-slate-500/30",
    maintenance:    "bg-slate-500/20 text-slate-500 border-slate-500/30",
  };
  return map[s] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
};

const emptyForm = {
  table_number: "",
  capacity: 4,
  zone: "Main",
  accessibility: false,
  high_chair: false,
  is_active: true,
};

const fmtClock = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function RestaurantTablesTab({ session, centers = [] }) {
  const token = session?.token;
  const isSuperAdmin = session?.is_super_admin === true;
  const isAdmin = session?.is_admin === true;
  const isAdminLike = isSuperAdmin || isAdmin;
  const userCenter = session?.center || "";
  const canPickCenter = isAdminLike;

  const [center, setCenter] = useState(canPickCenter ? "" : userCenter);
  const [tables, setTables] = useState([]);
  const [settings, setSettings] = useState({
    cleaning_duration_minutes: 15, auto_available_minutes: 0, auto_reset_previous_day: false,
  });
  const [userRoleForCenter, setUserRoleForCenter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingStatus, setEditingStatus] = useState(null);
  const [manualStatus, setManualStatus] = useState("available");
  const [statusReason, setStatusReason] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [oosDialog, setOosDialog] = useState(null);   // { table_number, reason }
  const [busyRow, setBusyRow] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  // Tick the "cleaning since" timers every 30s so the elapsed times feel live.
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const fetchTables = useCallback(async () => {
    if (!token || !center) { setTables([]); setUserRoleForCenter(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setTables(data.tables || []);
      setSettings(data.settings || { cleaning_duration_minutes: 15, auto_available_minutes: 0, auto_reset_previous_day: false });
      setUserRoleForCenter(data.user_role || null);
    } catch (err) {
      toast.error("Failed to load tables");
      setTables([]);
    } finally {
      setLoading(false);
    }
  }, [token, center]);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  // Rows this session is permitted to manage in the current center.
  const canManageThis = Boolean(userRoleForCenter?.can_manage_this_center);
  const canAdminThis  = Boolean(userRoleForCenter?.can_admin_this_center);

  // Structural (Table #, Capacity, Zone) is admin-only.
  const canEditStructural = canAdminThis;

  const staleRows = useMemo(
    () => tables.filter((t) => t.status === "cleaning" && t.previous_day_cleaning),
    [tables],
  );

  const openCreate = () => {
    if (!canEditStructural) { toast.error("Only Admin/Super-Admin can create tables"); return; }
    setEditingKey(null);
    setEditingStatus(null);
    setForm(emptyForm);
    setManualStatus("available");
    setStatusReason("");
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditingKey(t.table_number);
    setEditingStatus(t.status || "available");
    setManualStatus(t.status || "available");
    setStatusReason("");
    setForm({
      table_number: t.table_number || "",
      capacity: t.capacity || 4,
      zone: t.zone || "Main",
      accessibility: !!t.accessibility,
      high_chair: !!t.high_chair,
      is_active: t.is_active !== false,
    });
    setShowDialog(true);
  };

  const saveTable = async () => {
    if (canEditStructural) {
      if (!form.table_number.trim()) { toast.error("Table number is required"); return; }
      if (form.capacity < 1) { toast.error("Capacity must be at least 1"); return; }
    }
    if (!center) { toast.error("Please select a center"); return; }
    setLoading(true);
    try {
      // Admins may rename / edit structural fields. CMs skip the upsert
      // altogether — they only push status/features through the dedicated
      // endpoint (upsert would fail on the /update-status role guard).
      if (canEditStructural) {
        if (editingKey && editingKey !== form.table_number) {
          await fetch(`${API}/api/booking-tokens/tables/delete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, center, table_number: editingKey }),
          });
        }
        const res = await fetch(`${API}/api/booking-tokens/tables/upsert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, center, ...form }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Save failed");
        }
      }
      // Push status via the dedicated route so booking-lifecycle rules run.
      if (editingKey && canManageThis && manualStatus !== editingStatus) {
        // Center Managers may not set occupied / reserved from this UI.
        if (!canAdminThis && !["available", "cleaning", "out_of_service"].includes(manualStatus)) {
          throw new Error("Occupied and Reserved are controlled by Booking Intelligence.");
        }
        if (manualStatus === "out_of_service" && !statusReason.trim()) {
          throw new Error("Reason is required when marking a table Out of Service.");
        }
        const sres = await fetch(`${API}/api/booking-tokens/tables/update-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token, center, table_number: form.table_number,
            status: manualStatus,
            reason: statusReason || "manual update via Edit Table",
          }),
        });
        if (!sres.ok) {
          const err = await sres.json().catch(() => ({}));
          throw new Error(err.detail || "Status update failed");
        }
      }
      toast.success(editingKey ? "Table updated" : "Table created — status: Available");
      setShowDialog(false);
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Failed to save table");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    if (!canEditStructural) { toast.error("Only Admin/Super-Admin can delete tables"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center, table_number: confirmDelete }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Delete failed");
      toast.success(`Deleted ${confirmDelete}`);
      setConfirmDelete(null);
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const autoSeed = async () => {
    if (!center) { toast.error("Please select a center"); return; }
    if (!canEditStructural) { toast.error("Only Admin/Super-Admin can seed tables"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/auto-seed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center }),
      });
      const data = await res.json();
      if (data.seeded) toast.success(`Seeded ${data.total} default tables`);
      else toast.info(data.message || "Tables already exist");
      fetchTables();
    } catch (err) {
      toast.error("Failed to seed tables");
    } finally {
      setLoading(false);
    }
  };

  const runRepair = async () => {
    if (!center) { toast.error("Please select a center"); return; }
    setRepairing(true);
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/repair-statuses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Repair failed");
      if (data.repaired > 0) {
        toast.success(`Repaired ${data.repaired} table(s) — reset to Available. Scanned ${data.scanned}.`);
      } else {
        toast.info(`Nothing to repair — every reserved/occupied table has an active booking. (Scanned ${data.scanned})`);
      }
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Failed to repair statuses");
    } finally {
      setRepairing(false);
    }
  };

  const quickAction = async (tableNumber, action, extra = {}) => {
    setBusyRow(tableNumber);
    try {
      let res;
      if (action === "mark-cleaning-complete") {
        res = await fetch(`${API}/api/booking-tokens/tables/mark-cleaning-complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, center, table_number: tableNumber }),
        });
      } else if (action === "restore-available") {
        res = await fetch(`${API}/api/booking-tokens/tables/update-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token, center, table_number: tableNumber,
            status: "available", reason: extra.reason || "restored from out-of-service",
          }),
        });
      } else if (action === "start-cleaning") {
        res = await fetch(`${API}/api/booking-tokens/tables/update-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token, center, table_number: tableNumber,
            status: "cleaning", reason: "manual — start cleaning",
          }),
        });
      } else if (action === "mark-out-of-service") {
        res = await fetch(`${API}/api/booking-tokens/tables/update-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token, center, table_number: tableNumber,
            status: "out_of_service", reason: extra.reason,
          }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Action failed");
      toast.success(`${tableNumber}: ${data.prev_status || "?"} → ${data.new_status || "?"}`);
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setBusyRow(null);
    }
  };

  const resetStaleCleaning = async () => {
    if (!center) return;
    setRepairing(true);
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/reset-stale-cleaning`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      toast.success(`Reset ${data.reset_count} previous-day cleaning table(s) to Available`);
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Reset failed");
    } finally {
      setRepairing(false);
    }
  };

  const saveSettings = async (next) => {
    try {
      const res = await fetch(`${API}/api/booking-tokens/tables/settings/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center, ...next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success("Table settings saved");
      setSettings(data.settings);
      fetchTables();
    } catch (err) {
      toast.error(err.message || "Save failed");
    }
  };

  const statusOptions = canAdminThis ? ADMIN_STATUSES : CM_STATUSES;

  return (
    <div className="space-y-4" data-testid="restaurant-tables-tab">
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          {canPickCenter && (
            <div className="flex-1">
              <Label className="text-xs">Center</Label>
              <Select value={center} onValueChange={setCenter}>
                <SelectTrigger className="h-9" data-testid="tables-center-select">
                  <SelectValue placeholder="Select center..." />
                </SelectTrigger>
                <SelectContent>
                  {centers.map((c) => (
                    <SelectItem key={c.code || c} value={c.code || c}>
                      {c.code || c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!canPickCenter && (
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">Center</div>
              <div className="font-semibold text-primary">{userCenter}</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={fetchTables} disabled={loading || !center}
              data-testid="tables-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            {canEditStructural && (
              <Button variant="outline" onClick={autoSeed} disabled={loading || !center}
                data-testid="tables-auto-seed-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Auto-seed 10
              </Button>
            )}
            {canAdminThis && (
              <Button variant="outline" onClick={runRepair} disabled={repairing || !center}
                data-testid="tables-repair-btn"
                title="Reset any table flagged reserved/occupied that has no active booking behind it.">
                {repairing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
                Repair Statuses
              </Button>
            )}
            {canAdminThis && (
              <Button variant="outline" onClick={() => setShowSettings(true)} disabled={!center}
                data-testid="tables-settings-btn"
                title="Cleaning duration + auto-available rules for this centre.">
                <Settings className="w-4 h-4 mr-1" /> Cleaning Settings
              </Button>
            )}
            {canEditStructural && (
              <Button onClick={openCreate} disabled={!center}
                data-testid="tables-new-btn">
                <Plus className="w-4 h-4 mr-1" /> New Table
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Previous-day cleaning banner */}
      {staleRows.length > 0 && canManageThis && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex flex-col sm:flex-row sm:items-center gap-2"
          data-testid="tables-stale-cleaning-banner">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <div className="text-sm flex-1">
            <strong>{staleRows.length}</strong> table{staleRows.length === 1 ? "" : "s"} still marked <em>Cleaning</em> from a previous day&nbsp;
            (<span className="font-mono">{staleRows.map(r => r.table_number).join(", ")}</span>).
            No active booking is linked — safe to reset.
          </div>
          <Button size="sm" onClick={resetStaleCleaning} disabled={repairing}
            data-testid="tables-stale-cleaning-reset">
            {repairing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Mark all Available
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {!center ? (
            <div className="text-center py-12 text-muted-foreground">
              Please select a center to view its tables
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Armchair className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No tables configured for <span className="font-semibold">{center}</span></p>
              {canEditStructural && (
                <p className="text-xs mt-1">Click &quot;Auto-seed 10&quot; for defaults or &quot;New Table&quot; to add custom layout</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Table #</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Zone</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tables.map((t) => {
                    const status = t.status || "available";
                    const overdue = t.cleaning_overdue;
                    const cleaningElapsedMin = t.cleaning_since
                      ? Math.max(0, Math.floor((nowTs - new Date(t.cleaning_since).getTime()) / 60000))
                      : null;
                    return (
                      <TableRow key={t.table_number} data-testid={`table-row-${t.table_number}`}>
                        <TableCell className="font-mono font-semibold">{t.table_number}</TableCell>
                        <TableCell>{t.capacity} pax</TableCell>
                        <TableCell>{t.zone || "—"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {t.high_chair && (
                              <Badge variant="outline" className="text-xs">
                                <Baby className="w-3 h-3 mr-1" /> High chair
                              </Badge>
                            )}
                            {t.accessibility && (
                              <Badge variant="outline" className="text-xs">
                                <Accessibility className="w-3 h-3 mr-1" /> Accessible
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={statusBadge(status)} data-testid={`table-status-${t.table_number}`}>
                              {status.replace(/_/g, " ")}
                            </Badge>
                            {status === "cleaning" && (
                              <div className="text-[10px] text-muted-foreground flex items-center gap-1"
                                data-testid={`table-cleaning-timer-${t.table_number}`}>
                                <Timer className="w-3 h-3" />
                                {t.cleaning_since ? (
                                  <>
                                    Since {fmtClock(t.cleaning_since)}
                                    {t.cleaning_expected_ready && <> · Ready {fmtClock(t.cleaning_expected_ready)}</>}
                                    {cleaningElapsedMin != null && <> · {cleaningElapsedMin}m</>}
                                  </>
                                ) : (
                                  <span className="italic">no timestamp</span>
                                )}
                                {overdue && (
                                  <Badge className="ml-1 bg-orange-500/20 text-orange-500 border-orange-500/30"
                                    data-testid={`table-cleaning-overdue-${t.table_number}`}>
                                    Overdue
                                  </Badge>
                                )}
                                {t.previous_day_cleaning && (
                                  <Badge className="ml-1 bg-amber-500/20 text-amber-500 border-amber-500/30">
                                    Previous day
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {t.is_active !== false ? (
                            <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {/* Quick actions */}
                            {status === "cleaning" && canManageThis && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-xs border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
                                disabled={busyRow === t.table_number}
                                onClick={() => quickAction(t.table_number, "mark-cleaning-complete")}
                                data-testid={`table-mark-clean-${t.table_number}`}>
                                {busyRow === t.table_number ? <Loader2 className="w-3 h-3 animate-spin" /> :
                                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Mark Cleaning Complete</>}
                              </Button>
                            )}
                            {status === "out_of_service" && canManageThis && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-xs border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
                                disabled={busyRow === t.table_number}
                                onClick={() => quickAction(t.table_number, "restore-available")}
                                data-testid={`table-restore-${t.table_number}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Available
                              </Button>
                            )}
                            {status === "available" && canManageThis && (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-500"
                                  disabled={busyRow === t.table_number}
                                  onClick={() => quickAction(t.table_number, "start-cleaning")}
                                  data-testid={`table-start-clean-${t.table_number}`}
                                  title="Start Cleaning">
                                  <Timer className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                                  disabled={busyRow === t.table_number}
                                  onClick={() => setOosDialog({ table_number: t.table_number, reason: "" })}
                                  data-testid={`table-oos-${t.table_number}`}
                                  title="Mark Out of Service">
                                  <Wrench className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEdit(t)}
                              data-testid={`table-edit-${t.table_number}`} title="Edit"
                              disabled={!canManageThis}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {canEditStructural && (
                              <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(t.table_number)}
                                data-testid={`table-delete-${t.table_number}`} title="Delete"
                                className="text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="table-crud-dialog">
          <DialogHeader>
            <DialogTitle>{editingKey ? `Edit Table ${editingKey}` : "New Table"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Table Number *</Label>
                <Input value={form.table_number}
                  onChange={(e) => setForm((f) => ({ ...f, table_number: e.target.value }))}
                  placeholder="e.g. T01, Window-2, VIP-Room"
                  disabled={!canEditStructural}
                  data-testid="table-form-number" />
              </div>
              <div>
                <Label>Capacity (pax) *</Label>
                <Input type="number" min="1" value={form.capacity}
                  onChange={(e) => setForm((f) => ({ ...f, capacity: parseInt(e.target.value, 10) || 1 }))}
                  disabled={!canEditStructural}
                  data-testid="table-form-capacity" />
              </div>
            </div>
            <div>
              <Label>Zone</Label>
              <Select value={form.zone} onValueChange={(v) => setForm((f) => ({ ...f, zone: v }))}
                disabled={!canEditStructural}>
                <SelectTrigger data-testid="table-form-zone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
              {!canEditStructural && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Table #, Capacity &amp; Zone are read-only for Center Managers.
                </p>
              )}
            </div>
            {/* Status editor — visible for anyone allowed to manage this centre. */}
            {editingKey && canManageThis && (
              <div>
                <Label>Status</Label>
                <Select value={manualStatus} onValueChange={setManualStatus}>
                  <SelectTrigger data-testid="table-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Occupied &amp; Reserved are set automatically by Booking Intelligence.
                </p>
                {manualStatus === "out_of_service" && (
                  <div className="mt-2">
                    <Label>Reason *</Label>
                    <Input value={statusReason} onChange={(e) => setStatusReason(e.target.value)}
                      placeholder="e.g. Broken leg, plumbing issue"
                      data-testid="table-form-oos-reason" />
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-6 pt-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch checked={form.high_chair}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, high_chair: v }))}
                  disabled={!canManageThis}
                  data-testid="table-form-high-chair" />
                <Label>High chair available</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.accessibility}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, accessibility: v }))}
                  disabled={!canManageThis}
                  data-testid="table-form-accessibility" />
                <Label>Wheelchair accessible</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                  disabled={!canEditStructural}
                  data-testid="table-form-active" />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveTable} disabled={loading || !canManageThis} data-testid="table-form-save">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingKey ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="table-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete table {confirmDelete}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This cannot be undone. Deletion is blocked only when this table is linked to an
            <strong> active </strong> booking, live-queue token, or seated guest. Past history
            never blocks deletion.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={loading}
              data-testid="table-delete-confirm">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Out-of-service reason dialog (from row quick action) */}
      <Dialog open={!!oosDialog} onOpenChange={(o) => !o && setOosDialog(null)}>
        <DialogContent data-testid="table-oos-dialog">
          <DialogHeader>
            <DialogTitle>Mark {oosDialog?.table_number} Out of Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Input value={oosDialog?.reason || ""}
              onChange={(e) => setOosDialog((d) => ({ ...d, reason: e.target.value }))}
              placeholder="e.g. Broken leg, waiting on repair"
              data-testid="table-oos-dialog-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOosDialog(null)}>Cancel</Button>
            <Button onClick={async () => {
                if (!(oosDialog?.reason || "").trim()) { toast.error("Reason is required."); return; }
                const tn = oosDialog.table_number;
                const rs = oosDialog.reason;
                setOosDialog(null);
                await quickAction(tn, "mark-out-of-service", { reason: rs });
              }}
              data-testid="table-oos-dialog-confirm">
              Mark Out of Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleaning Settings dialog (Admin only) */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent data-testid="table-settings-dialog">
          <DialogHeader>
            <DialogTitle>Cleaning Settings — {center}</DialogTitle>
          </DialogHeader>
          <TableSettingsForm
            settings={settings}
            onSave={async (next) => {
              await saveSettings(next);
              setShowSettings(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TableSettingsForm({ settings, onSave }) {
  const [dur, setDur] = useState(settings.cleaning_duration_minutes || 15);
  const [auto, setAuto] = useState(settings.auto_available_minutes || 0);
  const [prevDay, setPrevDay] = useState(!!settings.auto_reset_previous_day);
  const [saving, setSaving] = useState(false);
  return (
    <div className="space-y-4">
      <div>
        <Label>Cleaning duration per table (minutes)</Label>
        <Input type="number" min="1" max="240" value={dur}
          onChange={(e) => setDur(parseInt(e.target.value, 10) || 15)}
          data-testid="settings-cleaning-duration" />
        <p className="text-[10px] text-muted-foreground mt-1">Used to compute expected-ready time and the Overdue badge. Default: 15 minutes.</p>
      </div>
      <div>
        <Label>Auto-mark Cleaning Table as Available</Label>
        <Select value={String(auto)} onValueChange={(v) => setAuto(parseInt(v, 10))}>
          <SelectTrigger data-testid="settings-auto-available"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Off (Recommended) — manual Mark Cleaning Complete</SelectItem>
            <SelectItem value="10">After 10 minutes</SelectItem>
            <SelectItem value="15">After 15 minutes</SelectItem>
            <SelectItem value="20">After 20 minutes</SelectItem>
            <SelectItem value="30">After 30 minutes</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground mt-1">
          When enabled, the system may promote Cleaning → Available after the configured wait.
          Out-of-Service tables are never touched.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={prevDay} onCheckedChange={setPrevDay} data-testid="settings-auto-reset-prev-day" />
        <Label>Auto-reset previous-day Cleaning tables at opening time</Label>
      </div>
      <DialogFooter>
        <Button disabled={saving} onClick={async () => {
            setSaving(true);
            try {
              await onSave({
                cleaning_duration_minutes: dur,
                auto_available_minutes: auto,
                auto_reset_previous_day: prevDay,
              });
            } finally { setSaving(false); }
          }} data-testid="settings-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save Settings
        </Button>
      </DialogFooter>
    </div>
  );
}
